import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import Logo from '../../components/Logo';
import { notify } from '../../services/dialog';
import { restoreAccount } from '../../services/auth';
import {
  colors,
  hairline,
  screenPadding,
  spacing,
} from '../../theme';

/**
 * Where somebody lands after signing in to an account they asked us to delete.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SIGN-IN WORKED AT ALL
 * ---------------------------------------------------------------------------
 * The account is closed, not erased. The number was never retired, so the code
 * reached the same account and a session opened - and that session exists for
 * this screen alone. Every other request the app could make answers 403 while
 * the account is closed.
 *
 * Refusing the sign-in outright would read cleaner and be much worse. Somebody
 * who deleted an account by mistake would be told only "no account", with
 * nowhere to go, and the number would look free enough that the next tap made
 * a second empty account beside the first - with none of their bookings,
 * addresses or history on it, and no way to reach the ones that exist.
 *
 * ---------------------------------------------------------------------------
 * THE BUTTON IS HERE BECAUSE THIS IS WHERE THE QUESTION IS ASKED
 * ---------------------------------------------------------------------------
 * Not in a support email. Answering the code on their own number is the same
 * proof of ownership that closing the account required, and it has just been
 * given - so there is nobody left to ask.
 *
 * Nothing is rebuilt when they press it. The account was never emptied, so
 * restoring is the marker coming off: the bookings, the saved addresses, the
 * invoices and the history are all still there.
 */

type Props = {
  /** Called once the account is open again, so the app can carry on. */
  onRestored: () => void;
  onSignOut: () => void;
};

function AccountDeletedScreen({ onRestored, onSignOut }: Props) {
  const [restoring, setRestoring] = useState(false);

  /*
   * A failure leaves this screen up.
   *
   * `onRestored` is what sends the app back to the home page, so calling it
   * on a request that did not succeed would drop somebody into a working app
   * on an account that is still closed - where every screen fails on its own,
   * separately, with no explanation.
   */
  const restore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      await restoreAccount();
      onRestored();
    } catch {
      notify(
        'Account not restored',
        'Something went wrong and nothing has changed. Please try again, or contact support if it keeps happening.',
      );
    } finally {
      setRestoring(false);
    }
  };

  return (
    <View style={styles.screen}>
      <ScrollView
        contentContainerStyle={styles.page}
        showsVerticalScrollIndicator={false}
      >
        <Logo height={56} style={styles.logo} />

        <View style={styles.glyph}>
          <Icon name="trash" size={40} color={colors.inkMuted} />
        </View>

        <Text variant="titleLg" style={styles.centred}>
          This account was deleted
        </Text>

        <Text variant="body" color={colors.inkMuted} style={styles.centred}>
          You cannot book with this number.
        </Text>

        {/*
          Two cards used to sit here - one headed "Nothing has been erased",
          listing the bookings, addresses and invoices still on the account,
          and one on what to do if the deletion was intended.

          Both are gone at Vishal's request, and the first was the reason: a
          screen is not the place to recite a retention policy at somebody who
          has just asked to leave.

          What replaces it says the only thing this screen needs to say - the
          account can come back - without describing what is kept to make that
          possible. `/legal/delete-account` still states the retention in full,
          and it must: leaving it off a screen is a choice about emphasis,
          taking it off the published page would be a different thing entirely.
        */}
        <Text variant="body" color={colors.inkMuted} style={styles.centred}>
          Changed your mind? You can bring this account back.
        </Text>

        <View style={styles.actions}>
          <Button
            label={restoring ? 'Restoring…' : 'Restore my account'}
            onPress={restore}
            disabled={restoring}
          />
          <Button
            label="Sign out"
            variant="secondary"
            onPress={onSignOut}
            disabled={restoring}
          />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  page: {
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
    gap: spacing.md,
  },
  logo: {
    marginBottom: spacing.sm,
  },
  glyph: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    marginBottom: spacing.xs,
    ...hairline,
  },
  centred: {
    textAlign: 'center',
  },
  actions: {
    alignSelf: 'stretch',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
});

export default AccountDeletedScreen;
